import { useCallback, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CopyIcon, PrinterIcon, ShoppingBagIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchVoucher } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnSecondary } from '../ui/styles';
import { money } from '../../core/format/currency';
import { received } from '../inbox/inboxRules';
import { ledgerKindLabel, signedAmount, sourceLabel, untilLabel, validForLabel, voucherStatusBadge } from './voucherLabels';
import { printVoucher } from './voucherDocument';
import { VoucherActions } from './VoucherActions';
import type { RequestOptions } from '../../core/types/backend';
import type { Voucher } from '../../core/types/vouchers';

/**
 * Eén bon: saldo, waarvoor hij geldt, tot wanneer, en het volledige grootboek. Opnieuw printen kan
 * altijd. Een fout herstel je met een correctieregel; een bon die kwijt is, blokkeer je.
 */
export function VoucherDetail() {
  const {
    id = ''
  } = useParams();
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
    } | null;
  };
  const back = {
    to: `/be/cadeaubonnen${state?.from ?? ''}`,
    label: 'Alle cadeaubonnen'
  };
  const load = useCallback((opts: RequestOptions) => fetchVoucher(id, opts), [id]);
  const {
    data: voucher,
    setData,
    status,
    reload
  } = useLatestLoad(load);
  if (status === 'loading' && !voucher) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-5628" data-mp="xe1cit7esgub">
        <Spinner data-id="element-5629" data-mp="1l4ds8q8wbhvg" /> Cadeaubon laden…
      </div>;
  if (status === 'error' && !voucher) return <LoadFailed title="Deze cadeaubon kon niet laden" onRetry={reload} data-id="element-5630" data-mp="1pvm6bzl67rey" />;
  if (!voucher) return <MissingItem title="Cadeaubon niet gevonden" back={back} data-id="element-5631" data-mp="1zrmb8d8g4rhz" />;
  return <VoucherView voucher={voucher} back={back} onChanged={setData} data-id="element-5632" data-mp="1us9m1lu3r8c8" />;
}
type ViewProps = {
  voucher: Voucher;
  back: {
    to: string;
    label: string;
  };
  onChanged: (next: Voucher) => void;
};
function VoucherView({
  voucher: v,
  back,
  onChanged
}: ViewProps) {
  const {
    marketing,
    site
  } = useAdmin();
  const design = marketing.designs.find(d => d.template === 'giftcard');
  const [printing, setPrinting] = useState(false);
  const badge = voucherStatusBadge[v.status];
  // Nieuwste bovenaan: de laatste beweging is wat ze meestal zoekt.
  const ledger = [...v.ledger].sort((a, b) => b.at.localeCompare(a.at));
  const print = async () => {
    setPrinting(true);
    const ok = await printVoucher(v, design, site.salon.name);
    setPrinting(false);
    if (!ok) toast.error('Printen lukte niet', {
      description: 'Sta pop-ups toe voor deze site en probeer het opnieuw.'
    });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(v.code);
      toast.success('Code gekopieerd');
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: 'Selecteer de code en kopieer hem zelf.'
      });
    }
  };
  return <div data-id="element-5633" data-mp="3dqnzameh7bmh">
      <PageHeader title={`Cadeaubon ···${v.codeLast4}`} description={`${sourceLabel[v.source]}${v.buyerName ? ` · ${v.buyerName}` : ''} · ${received(v.createdAt)}`} back={back} actions={<button type="button" onClick={() => void print()} disabled={printing} aria-busy={printing} className={btnSecondary} data-id="element-5635" data-mp="2l43c7y519loq">
            {printing ? <Spinner data-id="element-5636" data-mp="2v0ptkcy5hum0" /> : <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5637" data-mp="2uzy29ebl6j1j" />}
            Printen of PDF
          </button>} data-id="element-5634" data-mp="3ds8xcm001cp3" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-5638" data-mp="2zshd43iw302n">
        <div className="space-y-6" data-id="element-5639" data-mp="22aqyo0x9z39f">
          {/* Wat er nog op staat wint: groot, met de status ernaast. */}
          <section aria-labelledby="voucher-balance" className="rounded-lg border border-line bg-white p-4 sm:p-6" data-id="element-5640" data-mp="279aoicezoz72">
            <div className="flex items-start justify-between gap-4" data-id="element-5641" data-mp="cuwwyw81ue9z">
              <h2 id="voucher-balance" className="text-[13px] text-muted" data-id="element-5642" data-mp="8ucx3kw3cwk5">
                Saldo
              </h2>
              <Badge tone={badge.tone} data-id="element-5643" data-mp="mm16vhivb8xy">{badge.label}</Badge>
            </div>
            <p className="mt-1 font-display text-[2.4rem] font-light leading-tight tabular-nums text-ink" data-id="element-5644" data-mp="hsovi9378yn5">{money(v.balanceCents)}</p>
            <p className="text-[13px] text-muted" data-id="element-5645" data-mp="wgt8qxjhghyj">van {money(v.initialCents)} bij uitgifte</p>
            <div className="mt-5 border-t border-line pt-4" data-id="element-5646" data-mp="rj9tpveyo15j">
              <VoucherActions voucher={v} onChanged={onChanged} data-id="element-5647" data-mp="15jkld0o3hjrr" />
            </div>
          </section>

          <Panel title="Geschiedenis" description="Alles wat er met deze bon gebeurde. Regels worden nooit gewijzigd of verwijderd." flush data-id="element-5648" data-mp="10laomlmq2b41">
            <ol className="divide-y divide-line" data-id="element-5649" data-mp="37crxy2cbpn9y">
              {ledger.map(e => <li key={e.id} className="flex items-start gap-4 px-4 py-3.5 sm:px-5" data-id="element-5650" data-mp="32dfcnm13oo9h">
                  <span className="min-w-0 flex-1" data-id="element-5651" data-mp="1hu3i1nuog9pj">
                    <span className="block text-[14px] text-ink" data-id="element-5652" data-mp="1msdcfw8kv6a3">{ledgerKindLabel[e.kind]}</span>
                    <span className="block text-[12px] text-muted" data-id="element-5653" data-mp="191hzo8uyk518">
                      {received(e.at)}
                      {e.note ? ` · ${e.note}` : ''}
                    </span>
                    {e.orderId && <Link to={`/be/bestellingen/${e.orderId}`} className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-5654" data-mp="z2spz3d7tqw2">
                        <ShoppingBagIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-5655" data-mp="q2vyktzfydxb" />
                        Bestelling bekijken
                      </Link>}
                  </span>
                  <span className={`shrink-0 text-[14px] tabular-nums ${e.amount < 0 ? 'text-muted' : 'text-ink'}`} data-id="element-5656" data-mp="2khgeyugqbnaw">{signedAmount(e.unit, e.amount)}</span>
                </li>)}
            </ol>
          </Panel>
        </div>

        <Panel title="Gegevens" data-id="element-5657" data-mp="17gef1s5sufq">
          <dl className="space-y-4 text-[14px]" data-id="element-5658" data-mp="3sg405iubajnt">
            <div data-id="element-5659" data-mp="3seav27zck7hx">
              <dt className="text-[13px] text-muted" data-id="element-5660" data-mp="3shyol74sktwn">Code</dt>
              <dd className="mt-1 flex items-center gap-2" data-id="element-5661" data-mp="3of1zcxkqxzjq">
                <span className="select-all break-all font-mono tracking-wide text-ink" data-id="element-5662" data-mp="3jfflclx3gf9v">{v.code}</span>
                <button type="button" onClick={() => void copy()} aria-label="Code kopiëren" className="rounded-md p-1.5 text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" data-id="element-5663" data-mp="jzsqig8m7a32">
                  <CopyIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5664" data-mp="b779p5ojndgv" />
                </button>
              </dd>
            </div>
            <div data-id="element-5665" data-mp="68dvfb1fnifn">
              <dt className="text-[13px] text-muted" data-id="element-5666" data-mp="66tcyjse7py5">Geldt voor</dt>
              <dd className="mt-0.5 text-ink" data-id="element-5667" data-mp="13otcbvk733q7">{validForLabel[v.validFor]}</dd>
            </div>
            <div data-id="element-5668" data-mp="ywji43c1rhi4">
              <dt className="text-[13px] text-muted" data-id="element-5669" data-mp="yw0bhirukfje">Geldig tot en met</dt>
              <dd className="mt-0.5 text-ink" data-id="element-5670" data-mp="169relltsj60q">{untilLabel(v.validUntil)}</dd>
            </div>
            {v.orderId && <div data-id="element-5671" data-mp="1b7swi3fhczam">
                <dt className="text-[13px] text-muted" data-id="element-5672" data-mp="1b9dp3ni8i85u">Gekocht in</dt>
                <dd className="mt-0.5" data-id="element-5673" data-mp="1g52mbk6s7cyz">
                  <Link to={`/be/bestellingen/${v.orderId}`} className="text-ink underline underline-offset-4 hover:text-clay" data-id="element-5674" data-mp="1k7z79hjsnl63">
                    Bestelling bekijken
                  </Link>
                </dd>
              </div>}
            {v.note && <div data-id="element-5675" data-mp="1u4bxg5g4rgw3">
                <dt className="text-[13px] text-muted" data-id="element-5676" data-mp="1yx51er3ryl74">Notitie</dt>
                <dd className="mt-0.5 whitespace-pre-line text-ink" data-id="element-5677" data-mp="1yxwyy7gxmt01">{v.note}</dd>
              </div>}
          </dl>
          {!design && <p className="mt-5 text-[12px] text-muted" data-id="element-5678" data-mp="27y3bnr4t3oy5">Geen ontwerp "Cadeaubon" in Ontwerpen: de print gebruikt de standaardtekst.</p>}
        </Panel>
      </div>
    </div>;
}